import { ExternalLink, Home, Loader, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import StatusPage from '../components/StatusPage';
import MercuryoWidget from '../components/onramp/MercuryoWidget';
import OnrampQuoteList from '../components/onramp/OnrampQuoteList';
import useOnrampQuotes from '../components/onramp/useOnrampQuotes';
import { executeContinuePayment, readContinuePaymentCheckout } from '../components/payment-history/paymentContinue';
import { onrampAPI } from '../services/api';
import { API_CODES } from '../services/apiCodes';
import { responseData } from '../services/apiResponse';
import { trackEvent } from '../services/analytics';

const TRANSAK_MIN_AMOUNT_USD = 25;
const CONFIGURED_ONRAMP_PROVIDER = (process.env.REACT_APP_ONRAMP_PROVIDER || '').trim().toUpperCase();
const SESSION_INIT_CACHE_TTL_MS = 10 * 1000;
const SUCCESSFUL_ORDER_NOTIFY_MAX_ATTEMPTS = 3;
const SUCCESSFUL_ORDER_NOTIFY_RETRY_DELAY_MS = 1000;
const PROVIDER_EARLY_ORDER_STATUSES = new Set(['not_started', 'payment_pending', 'order_created']);
const REPLACEABLE_ATTEMPT_STATUSES = new Set(['payment_pending', 'order_created']);
const sessionInitCache = new Map();

export function shouldReturnToKYC(code) {
  return [
    API_CODES.PHONE_IDENTITY_REQUIRED,
    API_CODES.KYC_REQUIRED,
    API_CODES.KYC_PENDING,
    API_CODES.KYC_RETRY_REQUIRED,
  ].includes(code);
}

export function isTrustedTransakOrigin(origin) {
  try {
    const url = new URL(origin);
    return url.protocol === 'https:' && (url.hostname === 'transak.com' || url.hostname.endsWith('.transak.com'));
  } catch {
    return false;
  }
}

export function isTrustedTransakMessage(event, iframe) {
  return isTrustedTransakOrigin(event?.origin) && Boolean(iframe?.contentWindow) && event.source === iframe.contentWindow;
}

const wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

export function shouldRedirectOnrampCheckout(checkout) {
  const presentation = String(
    checkout?.checkout_presentation || checkout?.checkoutPresentation || '',
  ).trim().toLowerCase();
  if (presentation === 'iframe') return false;
  if (presentation === 'redirect' || presentation === 'system_browser') return true;

  const provider = (checkout?.provider || 'TRANSAK').trim().toUpperCase();
  const serviceProvider = (checkout?.service_provider || '').trim().toUpperCase();
  if (provider === 'TRANSAK') return false;
  if (provider === 'MELD' && serviceProvider === 'TRANSAK') return false;
  return true;
}

export function shouldRetrySuccessfulOrderNotification(error) {
  const status = error?.response?.status;
  return !status || status === 408 || status === 425 || status === 429 || status >= 500;
}

export async function notifySuccessfulOrderWithRetry(payload) {
  let lastError;

  for (let attempt = 1; attempt <= SUCCESSFUL_ORDER_NOTIFY_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await onrampAPI.notifyOrderSuccessful(payload);
      const transactionStatus = (responseData(response)?.transaction_status || '').trim().toLowerCase();
      if (!PROVIDER_EARLY_ORDER_STATUSES.has(transactionStatus)) {
        return response;
      }
      lastError = new Error(`Provider order is still ${transactionStatus}`);
    } catch (error) {
      if (!shouldRetrySuccessfulOrderNotification(error)) {
        throw error;
      }
      lastError = error;
    }

    if (attempt < SUCCESSFUL_ORDER_NOTIFY_MAX_ATTEMPTS) {
      await wait(SUCCESSFUL_ORDER_NOTIFY_RETRY_DELAY_MS * attempt);
    }
  }

  throw lastError || new Error('Failed to confirm successful provider order');
}

export async function notifyOrderCreatedBestEffort(payload) {
  try {
    return await onrampAPI.notifyOrderCreated(payload);
  } catch {
    return null;
  }
}

export async function notifyOrderSuccessfulBestEffort(payload) {
  try {
    return await notifySuccessfulOrderWithRetry(payload);
  } catch {
    return null;
  }
}

export function buildOnrampSessionCacheKey(requestId, payload) {
  return JSON.stringify({
    requestId,
    selectionToken: payload.selection_token || '',
    provider: payload.provider || '',
    purposeCode: payload.purpose_code || '',
    confirmedCryptoAmount: String(payload.confirmed_crypto_amount ?? ''),
    fiatCurrency: (payload.fiat_currency || '').trim().toUpperCase(),
    email: (payload.email || '').trim().toLowerCase(),
    countryCode: (payload.country_code || '').trim().toUpperCase(),
    regionCode: (payload.region_code || '').trim().toUpperCase(),
    paymentMethod: (payload.payment_method || '').trim().toUpperCase(),
  });
}

function getCachedOnrampSession(requestId, payload, initialize) {
  const now = Date.now();
  const cacheKey = buildOnrampSessionCacheKey(requestId, payload);
  const cached = sessionInitCache.get(cacheKey);

  if (cached && now - cached.createdAt < SESSION_INIT_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = initialize().catch((error) => {
    const latest = sessionInitCache.get(cacheKey);
    if (latest?.promise === promise) {
      sessionInitCache.delete(cacheKey);
    }
    throw error;
  });

  sessionInitCache.set(cacheKey, {
    createdAt: now,
    promise,
  });

  return promise;
}

export function createOrContinueOnrampSession(requestId, payload) {
  return getCachedOnrampSession(requestId, payload, async () => {
    try {
      return await onrampAPI.createSession(payload);
    } catch (error) {
      const data = error.response?.data || {};
      const status = (data.status || '').trim().toLowerCase();
      const attemptId = data.remittance_transaction_id;
      const isOwnedReplaceableConflict =
        data.error === 'TX_CONFLICT' &&
        data.is_owner === true &&
        Boolean(attemptId) &&
        REPLACEABLE_ATTEMPT_STATUSES.has(status);

      if (!isOwnedReplaceableConflict) {
        throw error;
      }

      const continued = await executeContinuePayment({
        request_id: data.request_id || requestId,
        active_attempt_id: attemptId,
      });
      return { data: continued.checkout };
    }
  });
}

const TransakCheckout = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const marketplaceRoute = !location.pathname.startsWith('/transak-checkout/');
  const continuedCheckout = useMemo(
    () => (marketplaceRoute ? readContinuePaymentCheckout(requestId) : null),
    [marketplaceRoute, requestId],
  );
  const iframeRef = useRef(null);
  const successfulOrderNotificationRef = useRef(false);
  const checkoutCreatingRef = useRef(false);

  const [widgetUrl, setWidgetUrl] = useState(null);
  const [provider, setProvider] = useState(null);
  const [serviceProvider, setServiceProvider] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [loading, setLoading] = useState(!marketplaceRoute || Boolean(continuedCheckout));
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(marketplaceRoute && !continuedCheckout ? 'quotes' : 'loading');
  const [quoteMode, setQuoteMode] = useState(marketplaceRoute && !continuedCheckout);
  const normalizedServiceProvider = (serviceProvider || '').trim().toUpperCase();
  const quotePayload = useMemo(() => ({
    request_id: requestId,
    payment_amount_usd: Number(localStorage.getItem('confirmed_amount') || 0),
    fiat_currency: (localStorage.getItem('fiat_currency') || '').trim().toUpperCase(),
    country_code: (localStorage.getItem('selected_country_alpha2') || '').trim().toUpperCase(),
    region_code: (localStorage.getItem('selected_region_code') || '').trim().toUpperCase() || undefined,
  }), [requestId]);
  const hasQuoteInput = Boolean(
    quotePayload.payment_amount_usd > 0 && quotePayload.fiat_currency && quotePayload.country_code,
  );
  const quoteState = useOnrampQuotes({
    payload: quotePayload,
    enabled: quoteMode && hasQuoteInput,
  });

  useEffect(() => {
    successfulOrderNotificationRef.current = false;
    if (marketplaceRoute && continuedCheckout) {
      openCheckout(continuedCheckout);
      setLoading(false);
    } else if (!marketplaceRoute) {
      initOnrampSession();
    }
    // Session initialization is keyed by requestId and guarded by the request cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, marketplaceRoute, continuedCheckout]);

  useEffect(() => {
    // Listen for Transak widget postMessage events
    const handleMessage = async (event) => {
      // Only process messages from Transak
      if (!isTrustedTransakMessage(event, iframeRef.current)) return;

      const data = event.data;
      if (!data || !data.event_id) return;

      switch (data.event_id) {
        case 'TRANSAK_ORDER_CREATED':
          // Immediately lock transaction to prevent duplicate payments
          if (transactionId) {
            void notifyOrderCreatedBestEffort({
              remittance_transaction_id: transactionId,
            });
          }
          break;

        case 'TRANSAK_ORDER_SUCCESSFUL':
          if (transactionId && !successfulOrderNotificationRef.current) {
            successfulOrderNotificationRef.current = true;
            localStorage.setItem('remittance_transaction_id', transactionId);
            localStorage.setItem('request_id', requestId);
            setStatus('success');
            trackEvent('purchase', { provider: provider || 'TRANSAK' });

            void notifyOrderSuccessfulBestEffort({
              remittance_transaction_id: transactionId,
            });

            setTimeout(() => {
              navigate(`/success/${transactionId}`);
            }, 1500);
          }
          break;

        case 'TRANSAK_ORDER_FAILED':
          trackEvent('payment_failed', { provider: provider || 'TRANSAK', reason: 'failed' });
          setError('Payment failed. Please try again.');
          setStatus('failed');
          break;

        case 'TRANSAK_ORDER_CANCELLED':
          trackEvent('payment_failed', { provider: provider || 'TRANSAK', reason: 'cancelled' });
          setError('Payment was cancelled.');
          setStatus('failed');
          break;

        case 'TRANSAK_WIDGET_CLOSE':
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [transactionId, requestId, provider, navigate]);

  const initOnrampSession = async () => {
    try {
      successfulOrderNotificationRef.current = false;
      setLoading(true);
      setError(null);

      const continuedCheckout = readContinuePaymentCheckout(requestId);
      if (continuedCheckout) {
        const selectedProvider = (continuedCheckout.provider || 'TRANSAK').toUpperCase();
        const checkoutUrl = continuedCheckout.checkout_url || continuedCheckout.widget_url;
        setProvider(selectedProvider);
        setServiceProvider(continuedCheckout.service_provider || null);
        setWidgetUrl(checkoutUrl);
        setTransactionId(continuedCheckout.remittance_transaction_id);
        localStorage.setItem('remittance_transaction_id', continuedCheckout.remittance_transaction_id);
        localStorage.setItem('request_id', continuedCheckout.request_id || requestId);
        localStorage.setItem('onramp_provider', selectedProvider);
        if (shouldRedirectOnrampCheckout(continuedCheckout) && checkoutUrl) {
          setStatus('redirect');
          window.location.assign(checkoutUrl);
          return;
        }
        setStatus('widget');
        return;
      }

      const purposeCode = localStorage.getItem('purpose_code');
      const confirmedAmount = parseFloat(localStorage.getItem('confirmed_amount') || '0');

      if (!purposeCode || !confirmedAmount) {
        setError('Missing session data. Please go back and try again.');
        setStatus('failed');
        setLoading(false);
        return;
      }

      if (confirmedAmount < TRANSAK_MIN_AMOUNT_USD) {
        setError(`Transak minimum amount is $${TRANSAK_MIN_AMOUNT_USD}. Please go back and enter $${TRANSAK_MIN_AMOUNT_USD} or more.`);
        setStatus('failed');
        setLoading(false);
        return;
      }

      // Get user email from localStorage (stored during login)
      const email = localStorage.getItem('user_email') || null;
      const countryCode = localStorage.getItem('selected_country_alpha2') || undefined;
      const fiatCurrency = (localStorage.getItem('fiat_currency') || '').trim().toUpperCase() || undefined;

      // Create onramp session via backend. Backend may choose Transak, MELD, or another enabled provider.
      // confirmed_amount is in crypto (e.g. 25 USDC), not fiat
      const response = await createOrContinueOnrampSession(requestId, {
        provider: CONFIGURED_ONRAMP_PROVIDER || undefined,
        request_id: requestId,
        purpose_code: purposeCode,
        confirmed_crypto_amount: confirmedAmount,
        fiat_currency: fiatCurrency,
        email: email,
        country_code: countryCode,
      });

      const selectedProvider = (responseData(response).provider || 'TRANSAK').toUpperCase();
      setProvider(selectedProvider);
      setServiceProvider(responseData(response).service_provider || null);

      const checkoutUrl = responseData(response).checkout_url || responseData(response).widget_url;
      setWidgetUrl(checkoutUrl);
      setTransactionId(responseData(response).remittance_transaction_id);
      localStorage.setItem('remittance_transaction_id', responseData(response).remittance_transaction_id);
      localStorage.setItem('request_id', responseData(response).request_id || requestId);
      localStorage.setItem('onramp_provider', selectedProvider);
      if (shouldRedirectOnrampCheckout(responseData(response)) && checkoutUrl) {
        setStatus('redirect');
        window.location.assign(checkoutUrl);
        return;
      }
      setStatus('widget');
    } catch (err) {
      const data = err.response?.data || {};
      const errorCode = data.error || '';
      const detail = typeof data.detail === 'string' ? data.detail : '';

      if (shouldReturnToKYC(data.code)) {
        navigate(`/kyc/${requestId}`, { replace: true });
        return;
      }

      // If transaction already exists (409 / TX_CONFLICT)
      if (err.response?.status === 409 || errorCode === 'TX_CONFLICT') {
        const existingTxId = data.remittance_transaction_id;
        const txStatus = (data.status || '').toLowerCase();
        if (existingTxId) {
          setTransactionId(existingTxId);
          if (!data.is_owner) {
            setStatus('not_owner');
            setLoading(false);
            return;
          }
          const lockedStatuses = ['payment_received', 'processing', 'payout_pending', 'completed', 'kivi_completed'];
          if (lockedStatuses.includes(txStatus)) {
            localStorage.setItem('remittance_transaction_id', existingTxId);
            localStorage.setItem('request_id', data.request_id || requestId);
            setLoading(false);
            navigate(`/success/${existingTxId}`, { replace: true });
            return;
          }
          setError(detail || 'A previous payment attempt was not completed. Please try again from the review page.');
          setStatus('failed');
          setLoading(false);
          return;
        }
      }

      // Intent expired / completed / inactive — show specific message
      if (['INTENT_EXPIRED', 'INTENT_COMPLETED', 'INTENT_NOT_ACTIVE'].includes(errorCode)) {
        setError(detail || 'This payment request is no longer available.');
        setStatus('failed');
        setLoading(false);
        return;
      }

      // Not authenticated — redirect to login
      if (errorCode === 'NOT_AUTHENTICATED') {
        setError(detail || 'Please log in to continue.');
        setStatus('failed');
        setLoading(false);
        return;
      }

      // Use backend detail message if available, otherwise show generic message
      const errorMsg = detail || 'Failed to initialize payment';
      setError(errorMsg);
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const openCheckout = (checkout) => {
    const selectedProvider = (checkout.provider || 'TRANSAK').toUpperCase();
    const checkoutUrl = checkout.checkout_url || checkout.widget_url;
    setProvider(selectedProvider);
    setServiceProvider(checkout.service_provider || null);
    setWidgetUrl(checkoutUrl);
    setTransactionId(checkout.remittance_transaction_id);
    localStorage.setItem('remittance_transaction_id', checkout.remittance_transaction_id);
    localStorage.setItem('request_id', checkout.request_id || requestId);
    localStorage.setItem('onramp_provider', selectedProvider);
    if (shouldRedirectOnrampCheckout(checkout) && checkoutUrl) {
      setStatus('redirect');
      window.location.assign(checkoutUrl);
      return;
    }
    setStatus('widget');
  };

  const handleQuoteContinue = async (selectionToken) => {
    if (checkoutCreatingRef.current) return;
    checkoutCreatingRef.current = true;
    let checkoutOpened = false;
    setQuoteMode(false);
    setStatus('loading');
    setLoading(true);
    setError(null);
    try {
      const response = await onrampAPI.createSession({
        request_id: requestId,
        purpose_code: localStorage.getItem('purpose_code'),
        selection_token: selectionToken,
      });
      openCheckout(responseData(response));
      checkoutOpened = true;
    } catch (checkoutError) {
      const data = checkoutError.response?.data || {};
      if (shouldReturnToKYC(data.code)) {
        navigate(`/kyc/${requestId}`, { replace: true });
        return;
      }
      setError(data.detail || 'The selected quote is no longer available. Please refresh and choose again.');
      setStatus('failed');
    } finally {
      if (!checkoutOpened) checkoutCreatingRef.current = false;
      setLoading(false);
    }
  };

  if (quoteMode) {
    if (!hasQuoteInput) {
      return (
        <StatusPage
          variant="error"
          title="Quote unavailable"
          description="Return to the review page and confirm your amount, country, and payment currency."
          step={{ current: 4, total: 5 }}
          onBack={() => navigate(`/review/${requestId}`)}
        />
      );
    }
    if (quoteState.loading && !quoteState.response) {
      return (
        <Layout step={4} totalSteps={5} onBack={() => navigate(`/review/${requestId}`)}>
          <div className="space-y-4 py-10 text-center">
            <Loader className="mx-auto h-10 w-10 animate-spin text-cyan-700" />
            <h2 className="text-xl font-bold text-slate-900">Finding live quotes</h2>
            <p className="text-sm text-slate-500">Checking available onramps and payment methods.</p>
          </div>
        </Layout>
      );
    }
    if (quoteState.error && !quoteState.response) {
      return (
        <StatusPage
          variant="error"
          title="Quotes unavailable"
          description="Live payment quotes could not be loaded."
          step={{ current: 4, total: 5 }}
          onBack={() => navigate(`/review/${requestId}`)}
          actions={[
            { label: 'Retry', icon: RefreshCw, primary: true, onClick: quoteState.refresh },
          ]}
        />
      );
    }
    return (
      <Layout step={4} totalSteps={5} onBack={() => navigate(`/review/${requestId}`)}>
        <OnrampQuoteList
          response={quoteState.response}
          selectedOnrampId={quoteState.selectedOnrampId}
          selectedPaymentMethods={quoteState.selectedPaymentMethods}
          onSelectOnramp={quoteState.selectOnramp}
          onSelectPaymentMethod={quoteState.selectPaymentMethod}
          onRefresh={quoteState.refresh}
          onContinue={handleQuoteContinue}
          refreshing={quoteState.refreshing}
          refreshProgress={quoteState.refreshProgress}
          stale={quoteState.stale}
        />
      </Layout>
    );
  }

  if ((status === 'loading' || loading) && !error) {
    return (
      <Layout step={4} totalSteps={5} onBack={() => navigate(`/review/${requestId}`)}>
        <div className="text-center py-12 space-y-6">
          <Loader className="w-12 h-12 text-primary-600 mx-auto animate-spin" />
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Preparing Checkout</h2>
            <p className="text-gray-600">Setting up your secure payment session...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (status === 'success') {
    return (
      <StatusPage
        variant="success"
        title="Payment Successful"
        description="Redirecting to transaction details…"
        step={{ current: 5, total: 5 }}
        onBack={() => navigate(`/review/${requestId}`)}
      >
        <div className="flex justify-center py-2">
          <Loader className="w-5 h-5 text-primary-600 animate-spin" />
        </div>
      </StatusPage>
    );
  }

  if (status === 'in_progress') {
    return (
      <StatusPage
        variant="warning"
        title="Payment In Progress"
        description="A payment is already being processed for this request. Please wait for it to complete."
        step={{ current: 4, total: 5 }}
        onBack={() => navigate(`/review/${requestId}`)}
        actions={[
          { label: 'Home', icon: Home, onClick: () => { const t = localStorage.getItem('token'); navigate(t ? '/' : '/login'); } },
          { label: 'Check Status', primary: true, onClick: () => { if (transactionId) navigate(`/success/${transactionId}`); } },
        ]}
      >
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <p className="text-sm text-gray-600">If your previous payment was not completed, it will expire automatically and you can try again.</p>
        </div>
      </StatusPage>
    );
  }

  if (status === 'not_owner') {
    return (
      <StatusPage
        variant="blocked"
        title="Payment Unavailable"
        description="This payment request has already been handled by another sender. No action is required from you."
        step={{ current: 4, total: 5 }}
        onBack={() => navigate(`/review/${requestId}`)}
        actions={[{ label: 'Go to Home', icon: Home, onClick: () => { const t = localStorage.getItem('token'); navigate(t ? '/' : '/login'); } }]}
      />
    );
  }

  if (status === 'failed' || error) {
    return (
      <StatusPage
        variant="error"
        title="Payment Failed"
        description="We couldn't process your payment. Please try again or contact support if the issue persists."
        detail={error || undefined}
        step={{ current: 4, total: 5 }}
        onBack={() => navigate(`/review/${requestId}`)}
        actions={[
          { label: 'Retry', icon: RefreshCw, primary: true, onClick: () => {
            setError(null);
            if (marketplaceRoute) {
              setStatus('quotes');
              setQuoteMode(true);
            } else {
              setStatus('loading');
              initOnrampSession();
            }
          } },
        ]}
      />
    );
  }

  if (status === 'redirect') {
    return (
      <StatusPage
        variant="warning"
        title="Opening Checkout"
        description={`Continue securely with ${serviceProvider || provider || 'your payment provider'}.`}
        step={{ current: 4, total: 5 }}
        onBack={() => navigate(`/review/${requestId}`)}
        actions={[
          { label: 'Open Checkout', icon: ExternalLink, primary: true, onClick: () => { if (widgetUrl) window.location.assign(widgetUrl); } },
        ]}
      >
        <div className="flex justify-center py-2">
          <Loader className="w-5 h-5 text-primary-600 animate-spin" />
        </div>
      </StatusPage>
    );
  }

  // Widget mode - show Transak iframe
  return (
    <Layout step={4} totalSteps={5} onBack={() => navigate(`/review/${requestId}`)}>
      <div className="space-y-2">
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Complete KYC verification and payment securely via {serviceProvider || provider || 'your payment provider'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden -mx-6 -mb-2">
          {widgetUrl && (normalizedServiceProvider === 'MERCURYO' ? (
            <MercuryoWidget checkoutUrl={widgetUrl} />
          ) : (
            <iframe
              ref={iframeRef}
              src={widgetUrl}
              width="100%"
              style={{ border: 'none', height: 'calc(100vh - 200px)', minHeight: '720px' }}
              allow="clipboard-write; camera; microphone; payment"
              referrerPolicy="strict-origin-when-cross-origin"
              title={`${provider || 'Onramp'} Checkout`}
            />
          ))}
        </div>

        <p className="text-center text-xs text-gray-400">
          A service by Kivi3 Technologies Ltd — Licensed by Dubai International Financial Centre.
        </p>
      </div>
    </Layout>
  );
};

export default TransakCheckout;
